/**
 * LocalConnector — passthrough to a local directory.
 *
 * Copies a source directory into the mount point, **unless** the host fleet
 * already owns the bind at that target. Every filesystem call here is async and
 * deadline-bounded: `ctx.mountTarget` for the workspace connector is the rclone
 * FUSE mount inside the agent container, and a synchronous call against a
 * wedged FUSE mount parks the whole event loop in an uninterruptible kernel
 * wait — a hang, not an error, so `try/catch` never fires (issue #454).
 */

import { cp, mkdir, stat } from "node:fs/promises";
import { resolve } from "node:path";
import type {
  ConnectContext,
  ConnectorChangeEvent,
  ConnectorDeclaration,
  ConnectorHandle,
  FilesystemFace,
  SyncOptions,
  SyncResult,
  WatchHandle,
  WatchOptions,
} from "@skaile/workspaces/connectors";
import {
  AbstractConnector,
  createFsWatcher,
  ensureFleetMounted,
  isFleetManaged,
} from "@skaile/workspaces/connectors";
import { FsDeadlineError, fsDeadlineMs, withFsDeadline } from "@skaile/workspaces/core";

interface LocalState {
  /** Resolved absolute path to the source directory. */
  sourceDir: string;
  /** True when the host fleet owns the bind at the mount target — no copy is ever performed. */
  fleetManaged?: boolean;
}

/**
 * Deadline multiplier applied to the recursive copy.
 *
 * A single metadata call (`stat`, `mkdir`) that has not returned within the
 * standard deadline is a wedged mount. A recursive copy is different: its
 * honest duration scales with the tree, so the single-op deadline would
 * false-positive on a large — but healthy — source. The multiplier keeps the
 * copy bounded (≈5 min at the 15 s default) without capping legitimate work,
 * and still scales with `SKAILE_FS_DEADLINE_MS`.
 *
 * Note the deadline only bounds the *wait*: `fs.promises.cp` cannot be
 * cancelled, so a losing copy stays pending. That costs one handle, not the
 * event loop — which is why the copy must never run against a fleet-managed
 * mount in the first place (see `connect`).
 */
const COPY_DEADLINE_FACTOR = 20;

/**
 * Filesystem-face connector for local filesystem directories — copies the source directory
 * into the mount target on connect and syncs on demand.
 * No external dependencies; ideal for development and local file access.
 * @docLink packages/factory-assets/concepts#local-connector
 */
export class LocalConnector extends AbstractConnector {
  readonly name = "local";

  readonly filesystem: FilesystemFace = {
    sync: (handle: ConnectorHandle, options?: SyncOptions): Promise<SyncResult> => {
      return this._sync(handle, options);
    },
  };

  async connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle> {
    const targetDir = ctx.mountTarget;
    if (!targetDir) {
      throw new Error("LocalConnector requires ctx.mountTarget to be set by the manager");
    }

    const started = Date.now();
    const source = declaration.mount?.source ?? "";
    const sourceDir = resolve(String(source));

    // Host fleet owns the bind at `targetDir` — mirror the sharepoint/webdav
    // drivers: verify the bind and do nothing else. Refusing the copy is the
    // point. `fs.promises.cp` has no per-file timeout, so an async recursive
    // copy across a wedged FUSE mount still leaves an uncancellable operation
    // pending; on a fleet-managed target the content is already there and the
    // copy would be pure downside (and, source/target overlapping physically,
    // potentially destructive).
    if (isFleetManaged(declaration.id)) {
      this.log.info("mount handled by host fleet — skipping local copy", {
        connectorId: declaration.id,
        targetDir,
      });
      await ensureFleetMounted(targetDir, this.log);
      this.log.info("mount ok", { targetDir, durationMs: Date.now() - started });
      return this.makeHandle(
        declaration,
        { sourceDir, fleetManaged: true } satisfies LocalState,
        targetDir,
      );
    }

    this.log.info("mount start", { source, targetDir });
    await this.assertSourceExists(sourceDir);

    await withFsDeadline(`mkdir ${targetDir}`, () => mkdir(targetDir, { recursive: true }));
    await this.copyTree(sourceDir, targetDir);

    this.log.info("mount ok", { targetDir, durationMs: Date.now() - started });
    return this.makeHandle(declaration, { sourceDir } satisfies LocalState, targetDir);
  }

  async disconnect(_handle: ConnectorHandle): Promise<void> {
    this.log.info("unmount");
    // No-op for local filesystem — don't delete the mount directory
  }

  private async _sync(handle: ConnectorHandle, _options?: SyncOptions): Promise<SyncResult> {
    this.log.debug("sync start");
    const state = handle.state as LocalState;

    // `sync` is reachable on demand (and from lifecycle hooks), not only via
    // connect — so it repeats the fleet refusal rather than trusting connect
    // to have been the only entry point.
    if (state.fleetManaged) {
      this.log.debug("sync skipped — fleet-managed");
      return { filesWritten: 0, filesDeleted: 0, bytesTransferred: 0 };
    }

    const src = resolve(state.sourceDir);
    const tgt = resolve(handle.mountPath!);

    if (src === tgt || tgt.startsWith(`${src}/`)) {
      this.log.debug("sync ok (no-op: source equals target)");
      return { filesWritten: 0, filesDeleted: 0, bytesTransferred: 0 };
    }

    await withFsDeadline(`mkdir ${tgt}`, () => mkdir(tgt, { recursive: true }));
    await this.copyTree(src, tgt);

    this.log.debug("sync ok");
    return { filesWritten: 0, filesDeleted: 0, bytesTransferred: 0 };
  }

  /**
   * Fail with the historical message when the source directory is missing,
   * while letting a wedged-mount deadline surface as itself.
   *
   * The old `existsSync` probe could not tell those apart — and on a hung
   * mount it never returned at all.
   */
  private async assertSourceExists(sourceDir: string): Promise<void> {
    try {
      await withFsDeadline(`stat ${sourceDir}`, () => stat(sourceDir));
    } catch (err) {
      if (err instanceof FsDeadlineError) {
        this.log.error("connect failed: source path unreadable (deadline)", err, { sourceDir });
        throw err;
      }
      this.log.error("connect failed: source path does not exist", undefined, { sourceDir });
      throw new Error(`Local source path does not exist: ${sourceDir}`);
    }
  }

  /**
   * Recursively copy `src` into `tgt`, skipping the copy when the two overlap.
   *
   * The string-overlap guard is a second line of defence behind the
   * fleet-managed refusal in `connect`: it is a *textual* comparison, so a
   * source that differs textually while overlapping physically (symlink, bind
   * mount, `..` segments already normalised away) still slips past it. It stays
   * because it is free and catches the common container layout.
   */
  private async copyTree(src: string, tgt: string): Promise<void> {
    if (src === tgt || tgt.startsWith(`${src}/`)) return;
    await withFsDeadline(
      `cp -r ${src} -> ${tgt}`,
      () => cp(src, tgt, { recursive: true }),
      fsDeadlineMs() * COPY_DEADLINE_FACTOR,
    );
  }

  override watch(
    handle: ConnectorHandle,
    callback: (event: ConnectorChangeEvent) => void,
    options?: WatchOptions,
  ): WatchHandle {
    let fsWatcher: WatchHandle | null = null;
    let closed = false;

    createFsWatcher(handle.mountPath!, callback, options)
      .then((w) => {
        // close() may have already run before creation resolved — dispose
        // immediately so the late watcher does not leak.
        if (closed) void w.close();
        else fsWatcher = w;
      })
      .catch((err) => {
        this.log.warn("watcher creation failed", {
          error: err instanceof Error ? err.message : String(err),
        });
      });

    return {
      close: async () => {
        closed = true;
        await fsWatcher?.close();
      },
    };
  }
}

/**
 * Creates a new LocalConnector instance.
 * @returns Configured LocalConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#local-connector-factory
 */
export function createConnector(): LocalConnector {
  return new LocalConnector();
}
